import apiClient from "@/lib/api/api-client";
import { API_ERROR_CODES, parseApiError } from "@/lib/api/api-error";
import type { AccessRole } from "@/lib/api/portal-access-role";
import { env } from "@/config/env";

export type PortalUserStatus = "pending" | "active" | "verified";

export type ExpectedDailyVolume =
  | "under_1k"
  | "1k_10k"
  | "10k_100k"
  | "100k_plus";

export interface PortalUser {
  id: string;
  contact_name: string;
  email: string;
  terms_accepted_at: string;
  status: PortalUserStatus;
  created_at: string;
  updated_at: string;
  company_name?: string;
  access_role?: AccessRole | null;
  intended_use_case?: string;
  expected_daily_volume?: ExpectedDailyVolume;
}

export interface LoginUserRequest {
  email: string;
  password: string;
  remember_me?: boolean;
}

export interface LoginUserResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: PortalUser;
}

export async function loginUser(payload: LoginUserRequest) {
  const { data } = await apiClient.post<LoginUserResponse>(
    env.authLoginPath,
    payload,
  );
  return data;
}

/**
 * Tries remember_me when requested; retries without it when the API rejects
 * the field (older backends). Client-side persistence is handled separately.
 */
export async function loginUserWithRememberMeFallback(
  payload: LoginUserRequest,
): Promise<LoginUserResponse> {
  if (!payload.remember_me) {
    return loginUser(payload);
  }

  const withoutRememberMe: LoginUserRequest = {
    email: payload.email,
    password: payload.password,
  };

  try {
    return await loginUser(payload);
  } catch (err) {
    const { status, code } = parseApiError(err);
    if (status === 400 && code === API_ERROR_CODES.invalidRequest) {
      return loginUser(withoutRememberMe);
    }
    throw err;
  }
}
